/**
 * The two-pass write that lands a parsed GEDCOM file in a tree.
 *
 * It is two passes because of one constraint: `bulk_create_people` mints person
 * ids server-side and *rejects* a client-supplied id, since a chosen id can
 * silently overwrite an existing person. A GEDCOM file refers to people by its
 * own xrefs (`@I1@`), so at the moment the people are created there is nothing
 * to point `parentIds` and `marriages[].spouseId` at.
 *
 *   Pass 1 — create everyone with their own attributes only, and build the
 *            xref → id map from the response.
 *   Pass 2 — rewrite the relationships in real ids and PATCH them on.
 *
 * Pass 2 depends on the bulk endpoint returning ids in the order the people
 * were sent. That is a deliberate guarantee of the API, pinned by
 * `TestBulkCreate::test_returned_ids_are_in_the_order_they_were_sent` — the
 * response carries no xrefs, so ordering is the only thing tying the two
 * together.
 *
 * The chunk sizes are the API's request ceilings, not tuning knobs.
 */

// people.MAX_BULK_CREATE — the most one create request may carry.
export const CREATE_CHUNK = 1000;

// batch_update_people refuses more than this: it is Firestore's 500-write
// batch limit.
export const UPDATE_CHUNK = 500;

const RETRY_STATUSES = new Set([429, 502, 503, 504]);

function chunk(items, size) {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Runs `attempt` once more after a pause if it failed for a reason that passes.
 *
 * An import is the one flow that can legitimately approach the write rate
 * limit — a 5,000-person file is a dozen or so requests in quick succession —
 * and losing half a landed import to a 429 would leave the tree in a state the
 * user has to unpick by hand. Anything else fails immediately; a quota refusal
 * or a validation error will not be different the second time.
 */
async function withRetry(attempt) {
  try {
    return await attempt();
  } catch (error) {
    if (!RETRY_STATUSES.has(error?.status)) throw error;
    await delay(error.status === 429 ? 5000 : 1000);
    return attempt();
  }
}

/**
 * Writes parsed people into the active tree.
 *
 * `createPeople(dataList)` must resolve to `{ ids }` in input order, and
 * `updatePeople(updates)` takes `[{ personId, data }]`. They are injected
 * rather than imported so this can be tested without a network or a signed-in
 * user.
 *
 * Resolves — it does not reject — when the write stops partway. Partial
 * application is a real outcome of this endpoint: each chunk is its own
 * transaction, so hitting the tree's people limit lands the earlier chunks and
 * refuses the rest. Reporting that as a flat failure would send someone
 * re-importing people who are already there, so the result says what landed and
 * why it stopped, and the relationships among whoever did land are still wired
 * up.
 */
export async function runGedcomImport({
  people,
  createPeople,
  updatePeople,
  onProgress = () => {},
}) {
  const result = {
    created: 0,
    linked: 0,
    total: people.length,
    stopped: null, // { phase, message } when the import did not finish
  };

  if (!people.length) return result;

  // ── Pass 1: create ────────────────────────────────────────────────────────
  const idByXref = new Map();

  onProgress({ phase: 'creating', done: 0, total: people.length });

  for (const batch of chunk(people, CREATE_CHUNK)) {
    let ids;
    try {
      ({ ids } = await withRetry(() => createPeople(batch.map(p => p.data))));
    } catch (error) {
      result.stopped = { phase: 'creating', message: errorMessage(error) };
      break;
    }

    if (!Array.isArray(ids) || ids.length !== batch.length) {
      // Without a one-to-one positional match there is no way to know which id
      // belongs to which person, and guessing would attach relationships to the
      // wrong people. Stop with what is already mapped.
      result.stopped = {
        phase: 'creating',
        message: 'The server returned an unexpected response while creating people.',
      };
      break;
    }

    batch.forEach((person, i) => idByXref.set(person.xref, ids[i]));
    result.created += ids.length;
    onProgress({ phase: 'creating', done: result.created, total: people.length });
  }

  if (!idByXref.size) return result;

  // ── Pass 2: link ──────────────────────────────────────────────────────────
  // Relationships are filtered to people who actually landed. After a partial
  // pass 1 the rest do not exist, and pointing at them would leave dangling
  // references that the canvas and the delete sweep both have to cope with.
  const updates = [];
  for (const person of people) {
    const personId = idByXref.get(person.xref);
    if (!personId) continue;

    const data = {};
    const parentIds = mapIds(person.rel.parents, idByXref);
    const children = mapIds(person.rel.children, idByXref);
    const marriages = person.rel.marriages
      .filter(m => idByXref.has(m.spouseXref))
      .map(m => ({
        spouseId: idByXref.get(m.spouseXref),
        marriageDate: m.marriageDate,
        marriagePlace: m.marriagePlace,
        status: m.status,
        endReason: m.endReason,
        endDate: m.endDate,
        children: mapIds(m.children, idByXref),
      }));

    if (parentIds.length) data.parentIds = parentIds;
    if (children.length) data.children = children;
    if (marriages.length) data.marriages = marriages;
    if (Object.keys(data).length) updates.push({ personId, data });
  }

  if (!updates.length) return result;

  onProgress({ phase: 'linking', done: 0, total: updates.length });

  for (const batch of chunk(updates, UPDATE_CHUNK)) {
    try {
      await withRetry(() => updatePeople(batch));
    } catch (error) {
      result.stopped = { phase: 'linking', message: errorMessage(error) };
      break;
    }
    result.linked += batch.length;
    onProgress({ phase: 'linking', done: result.linked, total: updates.length });
  }

  return result;
}

function mapIds(xrefs, idByXref) {
  const ids = [];
  for (const xref of xrefs) {
    const id = idByXref.get(xref);
    if (id) ids.push(id);
  }
  return ids;
}

function errorMessage(error) {
  return error?.message || 'The import could not be completed.';
}
